module Client.PasswordReset

open Fable.Core
open Elmish
open Fable.React
open Fable.React.Props
open Shared.Domain
open Pages
open System
open Fable.Core.JsInterop
open Fetch
open Shared
open Elmish.Navigation

type private ZxcvbnFeedback =
    {
        warning: string
        suggestions: string []
    }

type private ZxcvbnResult = 
    {
        calc_time: int 
        crack_time: float
        crack_time_display: string 
        entropy: float 
        password: string
        feedback: ZxcvbnFeedback
        score: int
    }

[<Import("default", from="zxcvbn")>]
let private zxcvbn (password: string) (zxcvbnList: string []): ZxcvbnResult  = jsNative

let banned = [|
    "ILLY"
    "mycarepath"
    "Systems"
    "cyt"
    "CYT"
    "Change Your Tomorrow"
|]

type Model = 
    { 
        Id: Guid
        Email: EmailEntry option 
        ErrorMsg: string
        Password: string
        Failed: bool
        PasswordScore: int
        PasswordWarning: string
        PasswordSuggestion: string[]
    }

/// The messages processed during managing the name 
type Msg =
  | ClickUpdatePassword
  | SetPassword of string
  | UpdateError of exn
  | PasswordUpdateResult of Result<unit, string list>
  | UserUpdatedOnClient
  | PasswordResetFailed
  | FoundPasswordReset of UserEmail

let getPasswordResetCmd id = 
    let url = Urls.PerformResetPassword id
    FetchRequest.get url FoundPasswordReset UpdateError

let updatePasswordCmd id password = 
    let url = Urls.RequestResetEmail
    let ob = { Id = id; Password = password }
    FetchRequest.put url ob (PasswordUpdateResult) UpdateError

let init id =
    {
        Id = id
        Email = None
        ErrorMsg = ""
        Password = ""
        Failed = false
        PasswordScore = -1
        PasswordWarning = ""
        PasswordSuggestion = [||]
    }, getPasswordResetCmd id

let setUserInLocalStorageCmd (newUser: JWT) = 
    Cmd.OfFunc.either (BrowserLocalStorage.save "user") newUser (fun () -> UserUpdatedOnClient) UpdateError

let update (msg:Msg) model : Model*Cmd<Msg>*Option<string> = 
    match msg with
    | SetPassword password ->
        let pwInfo = zxcvbn password banned
        { model with Password = password; PasswordScore = pwInfo.score; PasswordWarning = pwInfo.feedback.warning; PasswordSuggestion = pwInfo.feedback.suggestions }, [], None
    | FoundPasswordReset emailEntry ->
        { model with Email = Some (Email emailEntry.Email) }, [], None
    | ClickUpdatePassword ->
        model, updatePasswordCmd (model.Id) (model.Password), None
    | UpdateError e ->
        { model with ErrorMsg = e.Message },Cmd.none, None
    | PasswordUpdateResult res ->
        match res with
        | Result.Ok _ ->
            model, Navigation.newUrl (toHash Page.Home), Some "Your password has been reset."
        | Result.Error errs ->
            { model with ErrorMsg = errs |> String.concat " " }, Cmd.none, None
    | PasswordResetFailed ->
        { model with Failed = true }, [], None
    | UserUpdatedOnClient -> model, Cmd.none, None

let renderNoRecord model (dispatch: Msg -> unit) =
    [
        nav [ ClassName "navbar navbar-default navbar-fixed-top navbar-inverse" ] []
        div [ ClassName "container-fluid" ] [
            div [ ClassName "homeBanner" ] [
                div [ ClassName "center" ] [
                    h1 [] [ str "Change Your Password" ]
                ]
            ]  
            div [ ClassName "alert alert-danger text-left"; Style [ MarginTop "20px" ] ] [
                p [] [
                    str "Your password could not be reset at this time."
                ]
                ul [] [
                    li [] [ str "Did you take more than one hour to activate the reset?" ]
                    li [] [ str "Have already reset your password?" ]
                    li [] [ str "Did you mis-type the URL in the browser?" ]
                ]
                p [] [
                    str "If you still need to reset your password then try again."
                ]
            ]
            a [ Href (toHash Page.Home) ] [ str "Go back to the home page" ]
        ]
    ]

let bar amt cls =
    div [ 
        ClassName ("progress-bar progress-bar-striped progress-bar-" + cls)
        Role "progressbar"
        Style [
            Width (amt.ToString() + "%")
        ]
    ] [
        span [ ClassName "sr-only" ] []
    ]
let renderEntry model (dispatch: Msg -> unit) = 
    [
        nav [ ClassName "navbar navbar-default navbar-fixed-top navbar-inverse" ] []
        div [ ClassName "container-fluid" ] [
            div [ ClassName "homeBanner" ] [
                div [ ClassName "center" ] [
                    h1 [] [ str "Change Your Password" ]
                ]
            ]  
            div [ Id "performPasswordReset" ] [      
                p [] [
                    str "Remember - don't share this password with anybody else or reuse one from another website."
                ]    
                form [
                    Role "form"
                    Id "newPassword"
                ] [
                    div [ ClassName "form-group" ] [
                        input [ 
                            Id "newPasswordEntry"
                            HTMLAttr.Type "password"
                            ClassName "form-control input-lg"
                            Placeholder "Enter new password"
                            AutoFocus true
                            OnChange (fun ev -> dispatch (SetPassword !!ev.target?value))
                            Keyboard.onEnter ClickUpdatePassword dispatch
                            MaxLength 255.
                        ]
                    ]
                    div [] [
                        if String.IsNullOrWhiteSpace model.ErrorMsg then
                            ()
                        else
                            yield div [ 
                                ClassName "alert alert-danger"
                                Style [
                                    MarginTop "10px"
                                ]
                            ] [ str model.ErrorMsg ]                    
                    ]
                    div [ ClassName "progress"; Style [ MarginTop "20px" ] ] [
                        if String.IsNullOrWhiteSpace model.Password then
                            ()
                        else
                            yield bar (match model.PasswordScore with 0 -> 20 | _ -> 40) "danger"
                            yield bar (match model.PasswordScore with 0 | 1 -> 0 | 2 -> 20 | _ -> 40) "warning"
                            yield bar (if model.PasswordScore = 4 then 20 else 0) "success"
                    ]
                    div [] [
                        if String.IsNullOrWhiteSpace (model.PasswordWarning) then
                            ()
                        else
                            yield div [ ClassName "alert alert-warning" ] [ str (model.PasswordWarning) ]
                    ]
                    div [] [
                        if model.PasswordSuggestion.Length = 0 then
                            ()
                        else
                            yield div [ ClassName "alert alert-info" ] [
                                yield strong [] [ str "Suggestions for a better password:" ]
                                for sug in model.PasswordSuggestion do
                                    yield li [] [ str sug ]     
                            ]
                    ]
                    button [
                        HTMLAttr.Type "button"
                        HTMLAttr.Disabled (String.IsNullOrWhiteSpace (model.Password) || model.PasswordScore < 3)
                        ClassName "btn btn-lg btn-block btn-primary"
                        Keyboard.onEnter ClickUpdatePassword dispatch
                        OnClick (fun _ -> dispatch ClickUpdatePassword)
                    ] [ str "Update Password" ]
                ]
            ]
        ]
    ]


let view model (dispatch: Msg -> unit) = 
    if model.Email.IsSome then
        renderEntry model dispatch
    else
        renderNoRecord model dispatch